/**
 * Semantic HTML heading-tag literals (`h1`-`h6`) -- the canonical home of the heading-element-tag decision -- and the
 * default outline tier of a section-level title.
 *
 * A NARROWER RANGE COMPOSES OFF `HeadingLevel` with `Extract<...>`, naming the tiers it keeps (`SectionTitleTag`,
 * `types/ui-text.ts`; `ErrorDisplay`'s `titleTag`). `MarkdownHeadingLevel`
 * (`spa/chats/frontend/src/assistant/AssistantPane.components.message.tsx`) re-spells `'h1' | 'h2' | 'h3'` locally, and
 * folding it is DECLINED on the cost rather than on effort: this member set is fixed by the HTML specification, so no
 * change in this repo can widen or narrow it, both spellings stay correct forever, and a fold would buy no drift
 * protection. This note exists so the next audit meets that reasoning instead of re-deriving the fold as unlanded work.
 *
 * `_HEADING_LEVELS` stays module-private: nothing reads the tuple at runtime, so exporting it would mint a
 * zero-consumer value export. Who reads which export is a search away and is not kept here.
 */
import type { SectionTitleTag } from '@federa/types/ui-text';

/** Ordered tuple of semantic HTML heading tags h1-h6. Tuple ordering is the structural document-outline order. */
const _HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

/** Type union of semantic HTML heading tags h1-h6, derived from `_HEADING_LEVELS` so the type stays bound to the runtime tuple. */
export type HeadingLevel = (typeof _HEADING_LEVELS)[number];

// SECTION_TITLE_TAG

/** A section-level title's tier when the page names none: under a section's own `h2`. `SectionHeading` and the state
 *  message's title both default to it. */
export const SECTION_TITLE_TAG_DEFAULT: SectionTitleTag = 'h3';
